import React from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { Floor } from "shared/types/Floor"
import { FloorPrice } from "shared/types/__generated__/graphql"
import { useNumberFormat } from "shared/ui/input/number/hooks/useNumberFormat"
import { Readonly } from "shared/ui/readonly/Readonly"

import { FloorTitle } from "entities/floors/ui/floor-title/FloorTitle"
import { FloorFormModel } from "features/floor-management/types/FloorFormModel"
import { FloorFormTabType } from "features/floor-management/types/FloorFormTabType"
import { ProjectPageSidebarActions } from "pages/project-page/ui/ProjectPageSidebarActions"
import { FloorForm } from "widgets/floor/floor-form/FloorForm"
import "widgets/floor/floor-form/styles/_floor-form.scss"
import { PriceTag } from "widgets/price-tag/PriceTag"

import "../../styles/_project-page-sidebar.scss"

export interface ProjectPageFloorSidebarViewModel {
  id: string

  floor: Floor
  floorFormModel: FloorFormModel

  isReadonly?: boolean
  isDisabled?: boolean

  hideControls?: boolean
  actions: Action[]
  autoFocus?: boolean

  productTotal: number
  price?: FloorPrice | null
  isLoadingPrice?: boolean

  sidebarActiveTab: string | null
  setSidebarActiveTab: (tab: string | null) => void
}

interface FloorSidebarHeaderProps {
  floor: Floor
  isReadonly?: boolean
  price?: FloorPrice | null
  productTotal: number
  isLoadingPrice?: boolean
}

const FloorSidebarHeader = React.memo(
  ({
    floor,
    isReadonly,
    price,
    productTotal,
    isLoadingPrice
  }: FloorSidebarHeaderProps) => {
    const { t } = useTranslation()
    const formattedProductTotal = useNumberFormat(productTotal)

    if (isReadonly) {
      return (
        <div className="floor-sidebar__header floor-sidebar__header--readonly">
          <Readonly.Root className="floor-sidebar__title">
            <Readonly.Label>{t("Floor.Floor")}</Readonly.Label>
            <Readonly.Value>
              <FloorTitle floor={floor} showClarificationRequestIndicator />
            </Readonly.Value>
          </Readonly.Root>

          <PriceTag
            price={price?.priceTotal}
            currency={price?.currency}
            isLoading={isLoadingPrice}
            isComplete={price?.isComplete}
            label={t("Price.TotalPrice")}
            className="floor-sidebar__price"
          />
          <Readonly.Root className="floor-sidebar__product-total">
            <Readonly.Label>{t("Project.ProductTotal")}</Readonly.Label>
            <Readonly.Value>{formattedProductTotal}</Readonly.Value>
          </Readonly.Root>
        </div>
      )
    }

    return (
      <div className="floor-sidebar__header floor-form-grid">
        <FloorForm.LabelInput />
        <FloorForm.PositionInput />
      </div>
    )
  }
)

export const ProjectPageFloorSidebarView = React.memo(
  ({
    id,
    floor,
    isReadonly,
    isDisabled,
    floorFormModel,
    actions,
    autoFocus,
    hideControls,
    price,
    productTotal,
    isLoadingPrice,
    sidebarActiveTab,
    setSidebarActiveTab
  }: ProjectPageFloorSidebarViewModel) => {
    return (
      <div
        className="project-page-sidebar project-page-sidebar--floor card card--elevated"
        data-testid="project-sidebar"
      >
        <FloorForm
          id={id}
          {...floorFormModel}
          isReadonly={isReadonly}
          isDisabled={isDisabled}
          className="project-page-sidebar__content"
        >
          <FloorSidebarHeader
            floor={floor}
            isReadonly={isReadonly}
            price={price}
            productTotal={productTotal}
            isLoadingPrice={isLoadingPrice}
          />
          <FloorForm.Tabs
            activeTab={
              (sidebarActiveTab as FloorFormTabType | null) ?? undefined
            }
            onTabChange={setSidebarActiveTab}
          />
        </FloorForm>

        {hideControls ? null : (
          <ProjectPageSidebarActions
            actions={actions}
            autoFocus={autoFocus}
            isIconOnly={isReadonly}
          />
        )}
      </div>
    )
  }
)
